import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceDot,
} from 'recharts';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { REVENUE_TYPES } from '../../../services/investmentPlanningService';
import {
  PERSONAL_REVENUE_TYPES,
  isPersonalRevenueType,
  buildRevenueTimeline,
  getDefaultGrowthPercentDisplay,
} from '../../../services/planningTax/revenueSchedule';
import RevenueLineForm, { REVENUE_FORM_EMPTY, RevenueAdjustmentsEditor } from './RevenueLineForm';

const PERSONAL_TYPE_OPTIONS = REVENUE_TYPES.filter((t) => PERSONAL_REVENUE_TYPES.includes(t.value));

const RevenuesPlanningTab = ({
  revenues = [],
  entities = [],
  assets = [],
  onAdd,
  onUpdate,
  onDelete,
  formatCurrency,
  currentYear,
  horizonYears = 25,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState(REVENUE_FORM_EMPTY);

  const personalRevenues = useMemo(
    () => revenues.filter((r) => isPersonalRevenueType(r.type)),
    [revenues],
  );

  const totalMonthly = personalRevenues.reduce((s, r) => s + (parseFloat(r.monthlyAmount) || 0), 0);

  const timeline = useMemo(
    () => buildRevenueTimeline(revenues, currentYear, horizonYears, { personalOnly: true }),
    [revenues, currentYear, horizonYears],
  );

  const chartData = timeline.map((row) => ({
    year: row.year,
    total: row.totalMonthly,
    hasAdjustment: (row.adjustmentLabels || []).length > 0,
    label: (row.adjustmentLabels || []).join(', '),
  }));

  const adjustmentYears = chartData.filter((d) => d.hasAdjustment);

  const handleTypeChange = (type) => {
    setForm((prev) => ({
      ...REVENUE_FORM_EMPTY,
      entityId: prev.entityId,
      type,
      annualGrowthPercent: String(getDefaultGrowthPercentDisplay({ type })),
    }));
  };

  const handleAssetLink = (assetId) => {
    const asset = assets.find((a) => a.id === assetId);
    if (!asset) {
      setForm((prev) => ({ ...prev, linkedAssetId: assetId }));
      return;
    }
    const typeLabel = REVENUE_TYPES.find((t) => t.value === form.type)?.label || form.type;
    setForm((prev) => ({
      ...prev,
      linkedAssetId: assetId,
      name: `${typeLabel} - ${asset.name}`,
      entityId: prev.entityId || asset.entityId || '',
    }));
  };

  const buildPayload = () => {
    const growth = parseFloat(form.annualGrowthPercent);
    return {
      ...form,
      monthlyAmount: parseFloat(form.monthlyAmount) || 0,
      annualGrowthPercent: Number.isFinite(growth) ? growth : undefined,
      adjustments: (form.adjustments || []).map((a) => ({
        ...a,
        effectiveYear: parseInt(a.effectiveYear, 10),
        monthlyAmount: parseFloat(a.monthlyAmount) || 0,
      })),
    };
  };

  const handleSave = () => {
    if (!form.type || !form.name.trim() || !form.entityId) return;
    const data = buildPayload();
    if (editingId) {
      onUpdate(editingId, data);
    } else {
      onAdd(data);
    }
    handleCancel();
  };

  const handleEdit = (item) => {
    setForm({
      name: item.name || '',
      type: item.type || 'salaire',
      entityId: item.entityId || '',
      monthlyAmount: String(item.monthlyAmount || ''),
      isGross: item.isGross !== false,
      notes: item.notes || '',
      linkedAssetId: item.linkedAssetId || '',
      reinvest: item.reinvest || false,
      reinvestTargetId: item.reinvestTargetId || '',
      sourceCorpEntityId: item.sourceCorpEntityId || '',
      dividendClass: item.dividendClass || (item.dividendEligible ? 'eligible' : 'non_eligible'),
      dividendEligible: item.dividendEligible || item.dividendClass === 'eligible',
      annualGrowthPercent: item.annualGrowthPercent != null
        ? String(item.annualGrowthPercent)
        : String(getDefaultGrowthPercentDisplay(item)),
      adjustments: (item.adjustments || []).map((a) => ({ ...a })),
    });
    setEditingId(item.id);
    setShowForm(true);
    setExpandedId(null);
  };

  const handleCancel = () => {
    setForm({ ...REVENUE_FORM_EMPTY, entityId: entities[0]?.id || '' });
    setShowForm(false);
    setEditingId(null);
  };

  const openForm = () => {
    setForm({ ...REVENUE_FORM_EMPTY, entityId: entities[0]?.id || '' });
    setEditingId(null);
    setShowForm(true);
  };

  const saveInlineAdjustments = (item, adjustments) => {
    onUpdate(item.id, { adjustments });
  };

  const getEntityName = (id) => entities.find((e) => e.id === id)?.name || '—';
  const getCorpName = (id) => entities.find((e) => e.id === id && e.type === 'corporation')?.name || '—';
  const getTypeName = (type) => REVENUE_TYPES.find((t) => t.value === type)?.label || type;

  return (
    <div className="space-y-6" data-testid="revenus-tab-content">
      <div className="bg-card border border-border rounded-xl p-5 md:p-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
              <Icon name="Banknote" size={20} style={{ color: 'var(--color-success)' }} />
              Revenus personnels
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              Total mensuel : <span className="font-semibold" style={{ fontFamily: 'var(--font-data)' }}>{formatCurrency(totalMonthly)}</span>
              {' '}/ annuel : <span className="font-semibold" style={{ fontFamily: 'var(--font-data)' }}>{formatCurrency(totalMonthly * 12)}</span>
            </p>
            <p className="text-xs text-muted-foreground mt-2 max-w-2xl">
              Les loyers et revenus d&apos;immeubles se gèrent dans Bilan → section Revenus locatifs.
            </p>
          </div>
          <Button size="sm" iconName="Plus" iconPosition="left" onClick={openForm} data-testid="add-personal-revenue-btn">
            Ajouter un flux
          </Button>
        </div>

        {showForm && (
          <RevenueLineForm
            form={form}
            setForm={setForm}
            editingId={editingId}
            entities={entities}
            assets={assets}
            formatCurrency={formatCurrency}
            typeOptions={PERSONAL_TYPE_OPTIONS}
            showGrowth
            showAdjustments
            showCorpSelector
            holderEntityFilter="personnel"
            currentYear={currentYear}
            horizonYears={horizonYears}
            onSave={handleSave}
            onCancel={handleCancel}
            onTypeChange={handleTypeChange}
            onAssetLink={handleAssetLink}
          />
        )}

        {personalRevenues.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            Aucun revenu personnel. Ajoutez salaire, dividendes, placements ou autres flux.
          </p>
        ) : (
          <div className="overflow-x-auto mt-4">
            <table className="w-full text-sm" data-testid="personal-revenues-table">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="pb-2 pr-3">Nom</th>
                  <th className="pb-2 pr-3">Type</th>
                  <th className="pb-2 pr-3">Bénéficiaire</th>
                  <th className="pb-2 pr-3">Inc.</th>
                  <th className="pb-2 pr-3 text-right">Mensuel</th>
                  <th className="pb-2 pr-3 text-right">Croissance %</th>
                  <th className="pb-2 pr-3">Ajustements</th>
                  <th className="pb-2 w-24" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {personalRevenues.map((item) => {
                  const growthDisplay = item.annualGrowthPercent != null
                    ? item.annualGrowthPercent
                    : getDefaultGrowthPercentDisplay(item);
                  const adjCount = (item.adjustments || []).length;
                  const expanded = expandedId === item.id;
                  return (
                    <React.Fragment key={item.id}>
                      <tr className="hover:bg-muted/30" data-testid={`personal-revenue-row-${item.id}`}>
                        <td className="py-2.5 pr-3 font-medium">{item.name}</td>
                        <td className="py-2.5 pr-3 text-muted-foreground">{getTypeName(item.type)}</td>
                        <td className="py-2.5 pr-3 text-muted-foreground">{getEntityName(item.entityId)}</td>
                        <td className="py-2.5 pr-3 text-muted-foreground">{item.sourceCorpEntityId ? getCorpName(item.sourceCorpEntityId) : '—'}</td>
                        <td className="py-2.5 pr-3 text-right font-medium" style={{ color: 'var(--color-success)', fontFamily: 'var(--font-data)' }}>
                          {formatCurrency(item.monthlyAmount)}
                        </td>
                        <td className="py-2.5 pr-3 text-right text-muted-foreground" style={{ fontFamily: 'var(--font-data)' }}>
                          {growthDisplay}%
                        </td>
                        <td className="py-2.5 pr-3">
                          <button
                            type="button"
                            className="text-xs text-primary hover:underline"
                            onClick={() => setExpandedId(expanded ? null : item.id)}
                            data-testid={`toggle-adjustments-${item.id}`}
                          >
                            {adjCount ? `${adjCount} ajustement(s)` : 'Planifier'}
                          </button>
                        </td>
                        <td className="py-2.5">
                          <div className="flex gap-1 justify-end">
                            <Button size="xs" variant="ghost" iconName="Pencil" onClick={() => handleEdit(item)} />
                            <Button size="xs" variant="ghost" iconName="Trash2" onClick={() => onDelete(item.id)} style={{ color: 'var(--color-destructive)' }} />
                          </div>
                        </td>
                      </tr>
                      {expanded && (
                        <tr>
                          <td colSpan={8} className="pb-4 px-2">
                            <RevenueAdjustmentsEditor
                              adjustments={item.adjustments || []}
                              onChange={(adjustments) => saveInlineAdjustments(item, adjustments)}
                              currentYear={currentYear}
                              horizonYears={horizonYears}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-xl p-5 md:p-6" data-testid="revenue-timeline-preview">
        <h3 className="text-base font-semibold text-foreground mb-1">Aperçu — total mensuel personnel</h3>
        <p className="text-xs text-muted-foreground mb-4">Projection avec croissance et ajustements planifiés sur {horizonYears} ans.</p>
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="year" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => formatCurrency(v)} width={72} tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(value) => [formatCurrency(value), 'Total mensuel']}
                labelFormatter={(y) => `Année ${y}`}
              />
              <Line type="monotone" dataKey="total" stroke="var(--color-primary)" strokeWidth={2} dot={false} />
              {adjustmentYears.map((pt) => (
                <ReferenceDot
                  key={pt.year}
                  x={pt.year}
                  y={pt.total}
                  r={5}
                  fill="var(--color-warning)"
                  stroke="var(--color-background)"
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default RevenuesPlanningTab;
